import { useEffect, useRef } from 'react';

/**
 * Submission feedback.
 *
 * Deliberately not auto-dismissing. A success carries the reference the
 * visitor may want to quote back, and a timed toast would take it away before
 * it could be written down; an error carries what to do instead. Both stay
 * until dismissed.
 *
 * `alert` for a failure so it interrupts, `status` for a success so it does
 * not. Focus moves to the toast either way, so a keyboard or screen-reader
 * user lands on the outcome rather than back on the form they just submitted.
 */
export function Toast({
  tone,
  children,
  onDismiss,
  dismissLabel,
}: {
  tone: 'success' | 'error';
  children: React.ReactNode;
  onDismiss: () => void;
  dismissLabel: string;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onDismiss]);

  return (
    <div className="toast-region">
      <div
        ref={ref}
        tabIndex={-1}
        role={tone === 'error' ? 'alert' : 'status'}
        aria-live={tone === 'error' ? 'assertive' : 'polite'}
        className={`toast toast--${tone}`}
      >
        <span className="toast__icon" aria-hidden="true">
          {tone === 'success' ? '✓' : '!'}
        </span>
        <div className="toast__body">{children}</div>
        <button
          type="button"
          className="toast__close"
          onClick={onDismiss}
          aria-label={dismissLabel}
        >
          ✕
        </button>
      </div>
    </div>
  );
}
